import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { ModalService } from '../../../base/shared/modal/modal.service';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { ToolbarModule } from 'primeng/toolbar';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import {
  SubscriptionDTO,
  CreateSubscriptionDTO,
  UpdateSubscriptionDTO,
  FilterDTO,
  FilterOperator,
  SortDirection
} from '../../../base/models/models.swagger.generated';
import { SubscriptionFormType } from './subscription-edit-forms';
import { PagedQueryModel } from '../../../base/shared/table/paged-query-model';
import { FilterMap } from '../../../base/shared/table/filter-map';
import { FilterMetadata } from 'primeng/api';
import { Subscription } from 'rxjs';
import { SubscriptionService } from './subscription.service';
import { AppErrorMessagesComponent } from '../../../base/error-messages/app-error-messages.component';
import { SearchFieldComponent } from '../../../base/shared/search-field/search-field.component';
import { DropdownModule } from 'primeng/dropdown';
import { TagModule } from 'primeng/tag';
import { CheckboxModule } from 'primeng/checkbox';
import { ToastService } from 'src/app/base/services/toast.service';
import { SpinnerService } from 'src/app/base/spinner/spinner.service';

@Component({
  selector: 'app-subscription',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    TableModule,
    ToolbarModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    ToastModule,
    TableModule,
    TranslateModule,

    SearchFieldComponent,
    DropdownModule,
    TagModule,
    CheckboxModule
  ],
  templateUrl: './subscription.component.html',
  styleUrls: ['./subscription.component.scss'],
  providers: [SubscriptionService]
})
export class SubscriptionComponent implements OnInit, OnDestroy {
  public data: SubscriptionDTO[] = [];
  public totalNoOfRecords: number = 0;
  public subscription: SubscriptionDTO;
  public itemDialog: boolean = false;
  public pagedQuery: PagedQueryModel = PagedQueryModel.getDefault();
  public form: FormGroup<SubscriptionFormType>;
  public globalFilters = ['id', 'name', 'price', 'type', 'isActive', 'priceType'];
  public columnFilters: { [key: string]: FilterMetadata | FilterMetadata[] } = {};
  private _subscription: Subscription = new Subscription();
  private searchText: string = '';
  public isEditMode: boolean = false;
  public usersDialog: boolean = false;
  public subscriptionUsers: any[] = [];
  public userPagedQuery: PagedQueryModel = PagedQueryModel.getDefault();
  public userColumnFilters: { [key: string]: FilterMetadata | FilterMetadata[] } = {};
  public userTotalNoOfRecords: number = 0;


  public typeOptions = [
    { label: 'Individual', value: 0 },
    { label: 'Business', value: 1 }
  ];

  public priceTypeOptions = [
    { label: 'Monthly', value: 0 },
    { label: 'Annually', value: 1 },
    { label: 'OneTime', value: 2 }
  ];

  public RoleOptions = [
    { label: 'None', value: 0 },
    { label: 'Admin', value: 1 },
    { label: 'Client Admin', value: 2 },
    { label: 'Web User', value: 3 },
    { label: 'Mobile User', value: 4 },
    { label: 'Rest Service', value: 5 },
  ];


  get formControls() {
    return this.form.controls;
  }

  constructor(
    private SubscriptionService: SubscriptionService,
    private router: Router,
    private fb: FormBuilder,
    private translateService: TranslateService,
    private modalService: ModalService,
    private toastService: ToastService,
    private spinnerService: SpinnerService
  ) { }

  ngOnInit() {
    this.loadData();
  }

  public get filterMatchModeOptions() {
    return FilterMap.getStringFilterMatchModeOptions();
  }

  private loadData() {
    this._subscription.add(
      this.SubscriptionService.getData(this.pagedQuery).subscribe((data) => {
        this.data = data.items;
        this.totalNoOfRecords = data.totalNumberOfItems;
        this.subscription = null;
        this.itemDialog = false;
      })
    );
  }

  private getFilters(): FilterDTO[] {
    const filters: FilterDTO[] = [];
    const addFilter = (field: string, operator: FilterOperator, value: any) => {
      filters.push(new FilterDTO({ field, operator, value, isComplex: false }));
    };

    if (this.searchText) {
      addFilter('name', FilterOperator.Contains, this.searchText);
    }

    Object.entries(this.columnFilters).forEach(([key, filter]) => {
      if (Array.isArray(filter)) {
        filter.forEach(f => f.value && addFilter(key, FilterMap.getFilterOperator(f.matchMode), f.value));
      } else if (filter?.value) {
        addFilter(key, FilterMap.getFilterOperator(filter.matchMode), filter.value);
      }
    });
    return filters;
  }

  public onFilter(event: any) {
    this.columnFilters = event.filters;
    this.pagedQuery.filters = this.getFilters();
    this.loadData();
  }

  public openNew() {
    this.subscription = null;
    this.initForm();
    this.isEditMode = false;
    this.itemDialog = true;
  }

  public editItem(item: SubscriptionDTO) {
    this.subscription = new SubscriptionDTO(item);
    this.isEditMode = true;
    this.initForm();
    this.itemDialog = true;
  }


  public deleteSubscription(subscription: SubscriptionDTO) {
    const dialog = this.modalService.showOkCancelDialog(
      'i18n.domain.subscription.messages.delete-confirm-title',
      'i18n.domain.subscription.messages.delete-confirm'
    );
    dialog.onOkSubject.subscribe(() => {
      this._subscription.add(
        this.SubscriptionService.deleteRecord(subscription.id).subscribe(() => {
          this.loadData();
        })
      );
    });
  }

  getRoleLabels(roleIds: number[]): string {
    if (!roleIds || roleIds.length === 0) {
      return '-';
    }

    return roleIds
      .map(roleId => {
        const match = this.RoleOptions.find(r => r.value === roleId);
        return match ? match.label : roleId.toString();
      })
      .join(', ');
  }


  editUser(user: any): void {
    console.log('Edit user clicked:', user);

  }


  public hideDialog() {
    this.itemDialog = false;
  }

  public saveSubscription() {
    if (this.form.valid) {
      const subscriptionData = this.form.getRawValue();
      if (subscriptionData.Id) {

        const updateDto = new UpdateSubscriptionDTO({
          id: subscriptionData.Id,
          name: subscriptionData.name,
          price: subscriptionData.price,
          isActive: subscriptionData.status === true,
          type: this.subscription?.type,
          priceType: this.subscription?.priceType,
          tierLevel: this.subscription?.tierLevel
        });
        this._subscription.add(
          this.SubscriptionService.updateRecord(updateDto).subscribe(() => {
            this.loadData();
          })
        );
      } else {

        const createDto = new CreateSubscriptionDTO({
          name: subscriptionData.name,
          price: subscriptionData.price,
          isActive: subscriptionData.status === true,
          type: subscriptionData.Type,
          priceType: subscriptionData.priceType,
          details: subscriptionData.description,
          tierLevel: subscriptionData.tierLevel
        });
        this._subscription.add(
          this.SubscriptionService.addRecord(createDto).subscribe(() => {
            this.loadData();
          })
        );
      }
    }
  }

  public onPageChange(event: any) {
    this.pagedQuery.skip = event.first;
    this.pagedQuery.take = event.rows;
    this.loadData();
  }

  public onSearchTextChanged(searchText: string) {
    this.searchText = searchText;
    const filters = this.getFilters();
    console.log('Search Text:', searchText);
    console.log('Generated Filters:', filters);
    this.pagedQuery.filters = filters;
    this.pagedQuery.skip = 0;
    this.loadData();
  }

  public onSort(event: any) {
    this.pagedQuery.sortBy = event.field;
    this.pagedQuery.sortDirection = event.order === 1 ? SortDirection.Ascending : SortDirection.Descending;
    this.loadData();
  }

  private initForm() {
    const { id, name, details, price, isActive, type, priceType, tierLevel } = this.subscription || {};


    this.form = this.fb.group<SubscriptionFormType>({
      Id: this.fb.control<number>(id || 0),
      name: this.fb.control<string>(name || '', Validators.required),
      price: this.fb.control<number>(price || 0, Validators.required),
      Type: this.fb.control<number>(type ?? 0, Validators.required),
      priceType: this.fb.control<number>(priceType ?? 0, Validators.required),
      status: this.fb.control<boolean>(isActive ?? true, Validators.required),
      description: this.fb.control<string>(details || '', Validators.required),
      tierLevel: this.fb.control<number>(tierLevel ?? 1, Validators.required)  // ← add this
    });

    if (this.subscription) {
      this.formControls.Type.disable();
      this.formControls.priceType.disable();
      this.formControls.price.disable();
    } else {
      this.formControls.Type.enable();
      this.formControls.priceType.enable();
      this.formControls.price.enable();
    }
  }


  viewUsers(subscription: SubscriptionDTO) {
    this.subscriptionUsers = [];
    this.usersDialog = true;
    this.subscription = subscription;

    this.userPagedQuery.skip = 0;
    this.userPagedQuery.take = 10;
    this.userPagedQuery.sortBy = 'firstname';
    this.userPagedQuery.sortDirection = SortDirection.Ascending;

    this._subscription.add(
      this.SubscriptionService.searchUserForSubscription(subscription.id, this.userPagedQuery)
        .subscribe(result => {
          this.subscriptionUsers = result.items;
          this.userTotalNoOfRecords = result.totalNumberOfItems;
        })
    );
  }

  public onUserSearchTextChanged(searchText: string) {
    this.userPagedQuery.skip = 0;
    this.userPagedQuery.take = 10;
    this.userPagedQuery.sortBy = 'firstname';
    this.userPagedQuery.sortDirection = SortDirection.Ascending;
    this.userPagedQuery.filters = [
      new FilterDTO({
        field: 'firstname',
        operator: FilterOperator.Contains,
        value: searchText,
        isComplex: false
      })
    ];

    this.loadUsers();
  }

  public onUserFilter(event: any) {
    this.userColumnFilters = event.filters;
    this.userPagedQuery.filters = this.getUserFilters();
    this.loadUsers();
  }

  public onUserPageChange(event: any) {
    this.userPagedQuery.skip = event.first;
    this.userPagedQuery.take = event.rows;
    this.loadUsers();
  }

  public onUserSort(event: any) {
    this.userPagedQuery.sortBy = event.field;
    this.userPagedQuery.sortDirection =
      event.order === 1 ? SortDirection.Ascending : SortDirection.Descending;
    this.loadUsers();
  }

  private getUserFilters(): FilterDTO[] {
    const filters: FilterDTO[] = [];
    Object.entries(this.userColumnFilters).forEach(([key, filter]) => {
      if (Array.isArray(filter)) {
        filter.forEach(f => {
          if (f.value) {
            filters.push(
              new FilterDTO({
                isComplex: false,
                field: key,
                operator: FilterMap.getFilterOperator(f.matchMode),
                value: f.value
              })
            );
          }
        });
      } else if (filter?.value) {
        filters.push(
          new FilterDTO({
            isComplex: false,
            field: key,
            operator: FilterMap.getFilterOperator(filter.matchMode),
            value: filter.value
          })
        );
      }
    });
    return filters;
  }


  private loadUsers() {
    this._subscription.add(
      this.SubscriptionService.searchUserForSubscription(this.subscription.id, this.userPagedQuery)
        .subscribe(result => {
          this.subscriptionUsers = result.items;
          this.userTotalNoOfRecords = result.totalNumberOfItems;
        })
    );
  }


  ngOnDestroy(): void {
    this._subscription?.unsubscribe();
  }
}

